/**
 * CompetitionView - Main Orchestration Component
 *
 * Refactored from 267 lines to ~120 lines by extracting:
 * - useCompetitionState: Local UI state management
 * - useCompetitionData: Data fetching and computed values
 * - CompetitionModals: Modal rendering
 * - CompetitionContent: Main content rendering
 *
 * This component now focuses solely on orchestration.
 */

import React from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingState } from './LoadingState';
import { ErrorState } from './ErrorState';
import { CompetitionContent } from './CompetitionContent';
import { CompetitionModals } from './CompetitionModals';
import { useCompetitionState } from './hooks/useCompetitionState';
import { useCompetitionData } from './hooks/useCompetitionData';

interface CompetitionViewProps {
  gameId: string;
  leagueId?: string;
  onBack: () => void;
}

export const CompetitionView: React.FC<CompetitionViewProps> = ({ gameId, leagueId, onBack }) => {
  const { toast } = useToast();
  const { user } = useAuth();

  // Local UI state (modals, leaderboard)
  const {
    modals,
    isLeaderboardOpen,
    openQuickPickModal,
    closeQuickPickModal,
    openClearModal,
    closeClearModal,
    openSuccessModal,
    closeSuccessModal,
    openLeaderboard,
    closeLeaderboard,
  } = useCompetitionState();

  // Data fetching and computed values
  const {
    pickemDetails,
    competitionData,
    isLoading,
    error,
    selections,
    handleTeamSelect,
    handlePropSelect,
    handleTieBreakerChange,
    clearSelections,
    quickPick,
    allQuestionsAnswered,
    buildSubmissionPayload,
    submitMutation,
    hasExistingSelections,
    submitButtonText,
    selectionResults,
    selectionPoints,
    timeDisplay,
    isViewOnly,
    isResultsMode,
  } = useCompetitionData({ gameId, onSuccessSubmit: openSuccessModal });

  // Action handlers
  const handleClear = () => {
    clearSelections();
    closeClearModal();
    toast({
      title: 'Your selections have been cleared!',
      className: 'bg-bet99-primary text-white border-bet99-primary',
    });
  };

  const handleQuickPick = () => {
    quickPick();
    closeQuickPickModal();
    toast({
      title: 'Quick picks selected!',
      description: 'Random selections have been made for all questions.',
    });
  };

  const handleSaveChanges = async () => {
    const payload = buildSubmissionPayload();

    if (payload.length === 0) {
      toast({
        title: 'No selections',
        description: 'Please make at least one selection before saving.',
        variant: 'destructive',
      });
      return;
    }

    await submitMutation.mutateAsync({ selections: payload });
  };

  // Loading state
  if (isLoading) {
    return <LoadingState />;
  }

  // Error state
  if (error) {
    return <ErrorState onBack={onBack} />;
  }

  return (
    <>
      {/* Main Content */}
      <CompetitionContent
        pickemDetails={pickemDetails}
        competitionData={competitionData}
        timeDisplay={timeDisplay}
        selections={selections}
        selectionResults={selectionResults}
        selectionPoints={selectionPoints}
        onBack={onBack}
        onTeamSelect={handleTeamSelect}
        onPropSelect={handlePropSelect}
        onTieBreakerChange={handleTieBreakerChange}
        onQuickPick={openQuickPickModal}
        onClear={openClearModal}
        onSave={handleSaveChanges}
        onLeaderboardClick={openLeaderboard}
        isViewOnly={isViewOnly}
        isResultsMode={isResultsMode}
        isSaving={submitMutation.isPending}
        canSubmit={allQuestionsAnswered()}
        submitButtonText={submitButtonText}
      />

      {/* All Modals */}
      <CompetitionModals
        isQuickPickOpen={modals.quickPick}
        isClearOpen={modals.clear}
        isSuccessOpen={modals.success}
        isLeaderboardOpen={isLeaderboardOpen}
        onCloseQuickPick={closeQuickPickModal}
        onCloseClear={closeClearModal}
        onCloseSuccess={closeSuccessModal}
        onCloseLeaderboard={closeLeaderboard}
        onConfirmQuickPick={handleQuickPick}
        onConfirmClear={handleClear}
        onReturnToLobby={onBack}
        gameId={gameId}
        leagueId={leagueId}
        currentUserId={user?.user_id}
        hasExistingSelections={hasExistingSelections}
      />
    </>
  );
};

export default CompetitionView;
